/**
 * Tracks which forms this attendee has submitted.
 *
 * Submissions are POSTed to the backend responses endpoint
 * (docs/vericast-events-api-spec.md §5). Because attendee auth is a later
 * phase, we also record the submitted form id locally so the "Completed"
 * state persists in the UI. If the API is unreachable, we fall back to
 * local-only recording so the demo still works.
 */
import { apiClient } from './api'
import { resolveEventId } from './eventService'

const KEY = 'vc-submitted-forms'

export function getSubmittedFormIds(): Set<string> {
  try {
    const raw = localStorage.getItem(KEY)
    return new Set<string>(raw ? JSON.parse(raw) : [])
  } catch {
    return new Set<string>()
  }
}

export function markFormSubmitted(id: string): void {
  const ids = getSubmittedFormIds()
  ids.add(id)
  localStorage.setItem(KEY, JSON.stringify([...ids]))
}

/** Submit a form response to the backend, falling back to local-only. */
export async function submitFormResponse(
  formId: string,
  values: Record<string, unknown>,
): Promise<void> {
  try {
    const eventId = await resolveEventId()
    await apiClient.post(`/events/${eventId}/forms/${formId}/responses`, {
      values,
    })
  } catch {
    // API unreachable (offline/demo) — record locally so the UI reflects it.
  } finally {
    markFormSubmitted(formId)
  }
}
